import React, { useEffect, useState } from 'react';
import { Bot, Cpu, Search, Database } from 'lucide-react';
import { tracking } from '../services/tracking';

interface AnalysisProps {
  onComplete: () => void;
}

const Analysis: React.FC<AnalysisProps> = ({ onComplete }) => {
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);

  const stages = [
    { text: "Analisando seu perfil financeiro...", icon: <Database className="w-8 h-8 text-blue-400" /> },
    { text: "Cruzando dados com 10.000 traders...", icon: <Search className="w-8 h-8 text-purple-400" /> },
    { text: "Identificando gargalos de lucro...", icon: <Cpu className="w-8 h-8 text-yellow-400" /> },
    { text: "Gerando seu Plano Personalizado...", icon: <Bot className="w-8 h-8 text-brand-neon" /> },
  ];

  useEffect(() => {
    tracking.trackEvent('analysis_start');

    // Non-linear loading progress
    // 0-30% (Fast - 1.5s)
    // 30-70% (Slow - 4s)
    // 70-100% (Fast - 1.5s)

    let current = 0;
    const totalTime = 7000; // 7 seconds total roughly
    const intervalTime = 50;

    const timer = setInterval(() => {
      setStage(prev => {
        // Logic to update text stage based on progress
        if (current < 20) return 0;
        if (current < 50) return 1;
        if (current < 80) return 2;
        return 3;
      });

      setProgress(old => {
        if (old >= 100) {
          clearInterval(timer);
          setTimeout(onComplete, 800);
          return 100;
        }

        // Speed logic
        // Slower logic for more dramatic effect
        let increment = 0.5;
        if (old < 30) increment = 0.8;      // Initial warmth
        else if (old < 70) increment = 0.2; // Deep thinking (Slow)
        else increment = 1.5;               // Final sprint

        current = old + increment;
        return Math.min(current, 100);
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-brand-dark relative overflow-hidden">
      {/* Background Matrix/Grid Effect */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.02)_1px,transparent_1px)] bg-[size:20px_20px]"></div>

      <div className="max-w-md w-full text-center space-y-10 relative z-10">

        {/* Radar/Scan Animation */}
        <div className="relative w-40 h-40 mx-auto">
          <div className="absolute inset-0 bg-brand-neon/10 rounded-full animate-ping duration-[3000ms]"></div>
          <div className="absolute inset-0 bg-brand-neon/5 rounded-full animate-pulse delay-700"></div>
          <div className="absolute inset-0 border border-brand-neon/30 rounded-full shadow-[0_0_50px_rgba(16,185,129,0.2)] flex items-center justify-center bg-slate-900 z-10 backdrop-blur-sm">
            {stages[stage].icon}
          </div>
        </div>

        {/* Text */}
        <div className="h-20 flex flex-col items-center justify-center space-y-2">
          <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
            {stages[stage].text}
          </h3>
          <p className="text-slate-400 text-sm animate-pulse">Processando {Math.floor(progress * 100).toLocaleString()?.slice(0, 3)} blocos de dados...</p>
        </div>

        {/* Bar */}
        <div className="space-y-3 px-4">
          <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden border border-slate-700/50">
            <div
              className="h-full bg-gradient-to-r from-brand-neon via-emerald-400 to-cyan-400 transition-all duration-300 ease-out shadow-[0_0_15px_rgba(16,185,129,0.5)]"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-brand-neon font-mono tracking-widest opacity-80">
            <span>SYSTEM_CHECK: OK</span>
            <span>{Math.floor(progress)}%</span>
          </div>
        </div>

        {/* Terminal Output */}
        <div className="pt-8 flex flex-col items-center gap-3 opacity-60 text-xs font-mono tracking-wider">
          <div className={`transition-all duration-500 ${progress > 20 ? 'text-brand-neon scale-105' : 'text-slate-600'}`}>
            &gt;&gt; MARKET_MATCH: {progress > 20 ? '94%' : 'SCANNING...'}
          </div>
          <div className={`transition-all duration-500 ${progress > 50 ? 'text-brand-neon scale-105' : 'text-slate-600'}`}>
            &gt;&gt; RISK_TOLERANCE: {progress > 50 ? 'CALCULATED' : 'WAITING...'}
          </div>
          <div className={`transition-all duration-500 ${progress > 80 ? 'text-brand-neon scale-105' : 'text-slate-600'}`}>
            &gt;&gt; PLAN_GENERATION: {progress > 80 ? 'READY' : 'PENDING...'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analysis;